import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { doc, getDoc, collection, getDocs, setDoc, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import LinkManager from './LinkManager';
import PackageManager from './PackageManager';
import ContentManager from './ContentManager';
import ToastContainer from './ToastContainer';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [stats, setStats] = useState({
    totalPackages: 0,
    totalLinks: 0,
    totalContent: 0,
    lastUpdate: new Date().toLocaleDateString('tr-TR')
  });
  const navigate = useNavigate();

  const showToast = (message, type = 'success', duration = 3000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    checkAuth();
    loadStats();
    
    // Listen for toast events from child components
    const handleToast = (event) => {
      showToast(event.detail.message, event.detail.type, event.detail.duration || 3000);
    };
    
    window.addEventListener('showToast', handleToast);
    return () => window.removeEventListener('showToast', handleToast);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (!user) {
          navigate('/admin/login');
          return;
        }

        // Admin kontrolü
        const adminDoc = await getDoc(doc(db, 'admins', user.email));
        
        if (!adminDoc.exists() || adminDoc.data().isActive !== true) {
          await auth.signOut();
          navigate('/admin/login?error=not_authorized');
          return;
        }

        setUser(user);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('Auth check error:', err);
      navigate('/admin/login');
    }
  };

  const loadStats = async () => {
    try {
      const packagesSnapshot = await getDocs(collection(db, 'packages'));
      const linksDoc = await getDoc(doc(db, 'links', 'download'));
      const contentSnapshot = await getDocs(collection(db, 'content'));
      
      setStats({
        totalPackages: packagesSnapshot.size,
        totalLinks: linksDoc.exists() ? 1 : 0,
        totalContent: contentSnapshot.size,
        lastUpdate: new Date().toLocaleDateString('tr-TR')
      });
    } catch (err) {
      console.error('Stats load error:', err);
    }
  };

  const initializeDefaultData = async () => {
    if (!window.confirm('Site içindeki mevcut paketleri ve içerikleri Firebase\'e aktarmak istediğinize emin misiniz?\n\nBu işlem sadece 1 kez yapılmalıdır!')) {
      return;
    }

    try {
      setIsInitializing(true);

      // Default paketler (sitedeki mevcut paketler)
      const defaultPackages = [
        { name: '6 AY PAKET', duration: '6 Ay', price: '1.300', originalPrice: '2.000', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: true, region: 'tr' },
        { name: '12 AY PAKET', duration: '12 Ay', price: '2.000', originalPrice: '3.000', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: false, region: 'tr' },
        { name: '12 + 3 AY PAKET', duration: '15 Ay', price: '2.500', originalPrice: '3.500', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: false, region: 'tr' },
        { name: '15 + 3 AY PAKET', duration: '18 Ay', price: '2.900', originalPrice: '4.000', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: false, region: 'tr' },
        { name: '3 AY PAKET', duration: '3 Ay', price: '€45', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '1 Kullanıcı', popular: true, region: 'eu' },
        { name: '6 AY PAKET', duration: '6 Ay', price: '€65', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '1 Kullanıcı', popular: false, region: 'eu' },
        { name: '12 AY PAKET', duration: '12 Ay', price: '€100', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '1 Kullanıcı', popular: false, region: 'eu' },
        { name: '12 AY PAKET', duration: '12 Ay', price: '€185', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '2 Kullanıcı', popular: false, region: 'eu' }
      ];

      // Paketleri ekle
      for (const pkg of defaultPackages) {
        await addDoc(collection(db, 'packages'), {
          ...pkg,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }

      // Default linkler
      await setDoc(doc(db, 'links', 'download'), {
        windowsPC: 'https://tinyurl.com/elitpc1',
        androidXC: 'https://tinyurl.com/elitmedia1',
        androidSmarters: 'https://tinyurl.com/elitmedia2',
        playStoreXC: 'https://play.google.com/store/apps/details?id=com.nathnetwork.elitplus',
        playStoreSmarters: 'https://play.google.com/store/apps/details?id=com.elitdigitalone.elitdigitaliptvbox',
        iosApp: 'https://apps.apple.com/us/app/000-player/id1665441224',
        updatedAt: new Date().toISOString()
      });

      // Default içerikler
      await setDoc(doc(db, 'content', 'homeContent'), {
        heroTexts: [
          { line1: 'KESİNTİSİZ YAYIN', line2: '4K KALİTE' },
          { line1: 'GÜÇLÜ ALTYAPI', line2: 'HIZLI ERİŞİM' },
          { line1: 'SINIRSIZ EĞLENCE', line2: 'HER CİHAZDA' }
        ],
        heroDescription: 'Binlerce içeriğe anında erişin. Türkiye\'nin en güçlü IPTV altyapısı.',
        stats: {
          channels: '45.000+',
          movies: '72.223+',
          series: '25.285+'
        },
        features: [
          { icon: '🎬', title: 'Zengin İçerik', description: 'En güncel filmler, popüler diziler ve dünya çapında binlerce canlı kanal.' },
          { icon: '⚡', title: 'Hızlı Aktivasyon', description: 'Ödemeniz onaylandığı an üyeliğiniz aktif edilir. Anında izlemeye başlayın.' },
          { icon: '🛡️', title: 'Kesintisiz Yayın', description: 'Anti-Freeze teknolojimiz ile donma ve takılma yaşamadan izleyin.' }
        ],
        featuredPackage: {
          name: '6 AY PAKET',
          price: '1.300',
          originalPrice: '2.000',
          features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu']
        },
        updatedAt: new Date().toISOString()
      });

      await setDoc(doc(db, 'content', 'aboutContent'), {
        story: 'Elit IPTV, 2020 yılında Berlin, Almanya\'da kurulmuş, dünya genelinde hizmet veren bir IPTV sağlayıcısıdır. Misyonumuz, kullanıcılarımıza en kaliteli, kesintisiz ve uygun fiyatlı IPTV deneyimini sunmaktır.',
        story2: 'Yüksek hızlı sunucularımız, gelişmiş teknoloji altyapımız ve 7/24 müşteri desteği ile binlerce mutlu müşteriye hizmet vermekteyiz. Türkiye ve Avrupa bölgesinde özel optimizasyonlar yaparak, en iyi izleme deneyimini garanti ediyoruz.',
        story3: 'Modern teknoloji, güvenli ödeme sistemleri ve kullanıcı odaklı yaklaşımımız ile sektörde öncü bir konumdayız. Her geçen gün kendimizi geliştirerek, müşterilerimize daha iyi hizmet sunmak için çalışıyoruz.',
        values: [
          { icon: '🎯', title: 'Güvenilirlik', description: '%99.9 uptime garantisi ile kesintisiz hizmet sunuyoruz. Sunucularımız 7/24 izlenmekte ve optimize edilmektedir.' },
          { icon: '💎', title: 'Kalite', description: 'Full HD ve 4K kalitede yayınlar, yüksek hızlı sunucular ve gelişmiş teknoloji ile en iyi deneyimi sunuyoruz.' },
          { icon: '🤝', title: 'Müşteri Odaklılık', description: '7/24 WhatsApp desteği, hızlı yanıt süreleri ve kişisel yaklaşım ile müşteri memnuniyetini ön planda tutuyoruz.' },
          { icon: '🔒', title: 'Güvenlik', description: '3D Secure ödeme sistemleri, SSL şifreleme ve güvenli veri saklama ile bilgilerinizi koruyoruz.' }
        ],
        updatedAt: new Date().toISOString()
      });

      await setDoc(doc(db, 'content', 'contactInfo'), {
        whatsapp: '+49-163-892-6744',
        email: 'elitelk2009@gmail.com',
        address: {
          street: 'Friedrichstraße 123',
          city: 'Berlin',
          postalCode: '10117',
          country: 'Almanya'
        },
        workingHours: '7/24 Açık',
        updatedAt: new Date().toISOString()
      });

      await setDoc(doc(db, 'content', 'seoSettings'), {
        siteTitle: 'Elit IPTV | Premium IPTV Hizmeti',
        siteDescription: 'Elit IPTV ile 50.000+ canlı TV kanalı, 72.000+ film ve dizi arşivine erişin. 4K Ultra HD kalite, kesintisiz yayın.',
        siteKeywords: 'iptv, iptv türkiye, iptv paket, canlı tv, film izle, dizi izle, 4k iptv',
        siteUrl: 'https://elitiptv.net',
        updatedAt: new Date().toISOString()
      });

      await loadStats();
      showToast('✅ Başarılı! Tüm paketler ve içerikler Firebase\'e aktarıldı. Şimdi Paket Yönetimi veya İçerik Yönetimi sekmelerinden düzenleyebilirsiniz.', 'success', 5000);
    } catch (err) {
      console.error('Initialize error:', err);
      showToast('❌ Hata: ' + err.message, 'error', 5000);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="admin-sidebar">
        <div className="admin-logo">
          <div className="logo-container">
            <img src="/logo.png" alt="ELIT IPTV" className="admin-logo-img" />
            <h2>Admin Panel</h2>
          </div>
        </div>
        
        {user && (
          <div className="admin-user-info">
            <img 
              src={user.photoURL || 'https://via.placeholder.com/40'} 
              alt="Avatar" 
              className="user-avatar"
            />
            <div className="user-details">
              <strong>{user.displayName || user.email}</strong>
              <span>{user.email}</span>
            </div>
          </div>
        )}

        <nav className="admin-nav">
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
            data-icon="📊"
          >
            <span className="nav-text">📊 Dashboard</span>
          </button>
          <button 
            className={activeTab === 'packages' ? 'active' : ''}
            onClick={() => setActiveTab('packages')}
            data-icon="📦"
          >
            <span className="nav-text">📦 Paket Yönetimi</span>
          </button>
          <button 
            className={activeTab === 'links' ? 'active' : ''}
            onClick={() => setActiveTab('links')}
            data-icon="🔗"
          >
            <span className="nav-text">🔗 Link Yönetimi</span>
          </button>
          <button 
            className={activeTab === 'content' ? 'active' : ''}
            onClick={() => setActiveTab('content')}
            data-icon="✏️"
          >
            <span className="nav-text">✏️ İçerik Yönetimi</span>
          </button>
        </nav>

        <div className="admin-footer">
          <button onClick={handleLogout} className="btn-logout">
            🚪 Çıkış Yap
          </button>
        </div>
      </div>

      <div className="admin-content">
        {activeTab === 'dashboard' && user && (
          <div className="welcome-banner">
            <div className="welcome-content">
              <div className="welcome-icon">✨</div>
              <div className="welcome-text">
                <h1>Hoş Geldiniz, Patron! 👋</h1>
                <p>Bugün de harika işler yapacağız! Admin panelinizde her şey hazır. 🚀</p>
              </div>
            </div>
            <div className="welcome-decoration">
              <div className="decoration-circle circle-1"></div>
              <div className="decoration-circle circle-2"></div>
              <div className="decoration-circle circle-3"></div>
            </div>
          </div>
        )}
        <div className="admin-header">
          <h1>
            {activeTab === 'dashboard' && '📊 Dashboard'}
            {activeTab === 'packages' && '📦 Paket Yönetimi'}
            {activeTab === 'links' && '🔗 Link Yönetimi'}
            {activeTab === 'content' && '✏️ İçerik Yönetimi'}
          </h1>
        </div>

        <div className="admin-main">
          {activeTab === 'dashboard' && (
            <div>
              <div className="dashboard-stats">
                <div className="stat-card">
                  <div className="stat-icon">📦</div>
                  <div className="stat-info">
                    <h3>{stats.totalPackages}</h3>
                    <p>Toplam Paket</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🔗</div>
                  <div className="stat-info">
                    <h3>{stats.totalLinks}</h3>
                    <p>Toplam Link</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📝</div>
                  <div className="stat-info">
                    <h3>{stats.totalContent}</h3>
                    <p>İçerik Sayfası</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🕒</div>
                  <div className="stat-info">
                    <h3>{stats.lastUpdate}</h3>
                    <p>Son Güncelleme</p>
                  </div>
                </div>
              </div>

              {stats.totalPackages === 0 && stats.totalContent === 0 && (
                <div className="init-section">
                  <div className="init-card">
                    <h3>🚀 İlk Kurulum</h3>
                    <p>Henüz paket ve içerik eklenmemiş. Site içindeki mevcut paketleri ve içerikleri Firebase'e aktarmak için aşağıdaki butona tıklayın.</p>
                    <button 
                      onClick={initializeDefaultData}
                      disabled={isInitializing}
                      className="btn-initialize"
                    >
                      {isInitializing ? '⏳ Aktarılıyor...' : '📥 Mevcut Paketleri ve İçerikleri Aktar'}
                    </button>
                    <small>Bu işlem sadece 1 kez yapılmalıdır. Sitedeki tüm paketler, linkler ve içerikler Firebase'e eklenecektir.</small>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'packages' && <PackageManager />}
          {activeTab === 'links' && <LinkManager />}
          {activeTab === 'content' && <ContentManager />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
